import React, { useEffect, useState } from "react";
import { MemoryStick, Activity, CheckCircle, Cpu, Package, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";

// LoadingSpinner placeholder
const LoadingSpinner = ({ section }: { section: string }) =>
    <div className="text-gray-200 text-xs mb-2 p-4">Loading {section}...</div>;

interface MemoryInfoProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

const MemoryInfo: React.FC<MemoryInfoProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
}) => {
    const [memoryInfo, setMemoryInfo] = useState<any>(null);
    const [modules, setModules] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

    async function fetchMemory() {
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({
                    client_id,
                    organization_id,
                    agent_id,
                    asset_id
                }),
            };

            const response = await baseapi.get("/asset_memory", { params });
            const data = response.data?.data?.[0];

            if (data) {
                setMemoryInfo(data);
                let parsedModules = [];
                try {
                    if (typeof data.modules === 'string') {
                        parsedModules = JSON.parse(data.modules);
                    } else if (Array.isArray(data.modules)) {
                        parsedModules = data.modules;
                    }
                } catch (err) {
                    console.error("Failed to parse memory modules JSON", err);
                }
                setModules(parsedModules);
            } else {
                setMemoryInfo(null);
                setModules([]);
            }

        } catch (e: any) {
            showError(e.message);
        }
        setLoading(false);
    }
    useEffect(() => {

        fetchMemory();
    }, [agent_id, asset_id, organization_id, client_id]);

    const toggleSection = (section: string) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (next.has(section)) next.delete(section);
            else next.add(section);
            return next;
        });
    };

    const memoryUsageWidth = memoryInfo?.memory_usage
        ? Math.min(parseFloat(memoryInfo.memory_usage as string), 100) + "%"
        : "0%";

    return (
        <div className="p-3">
            {loading && <LoadingSpinner section="memory" />}
            {!loading && (
                <>
                    <div className="flex items-center justify-between mb-4 border-b border-gray-700 pb-2">
                        <div className="flex items-center">
                            <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                                <MemoryStick className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <h2 className="text-sm font-bold text-white leading-tight">Memory Information</h2>
                                <p className="text-gray-200 text-[10px] uppercase font-semibold tracking-wider mt-0.5">
                                    {memoryInfo?.module_count ?? modules.length} modules • {memoryInfo?.total_ram || '-'} total • {memoryInfo?.memory_usage || '-'} used
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => fetchMemory()}
                                disabled={loading}
                                className="p-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-600 rounded-md transition-colors disabled:opacity-50"
                                title="Refresh data"
                            >
                                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                        </div>
                    </div>

                    {memoryInfo ? (
                        <div className="space-y-3">
                            {/* Memory Overview Cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 rounded-md border border-purple-500/20 p-2.5">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-purple-300 text-[10px] font-medium uppercase">Total RAM</p>
                                            <p className="text-white text-sm font-bold">{memoryInfo.total_ram}</p>
                                        </div>
                                        <MemoryStick className="w-4 h-4 text-purple-400 opacity-70" />
                                    </div>
                                </div>
                                <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-md border border-blue-500/20 p-2.5">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-blue-300 text-[10px] font-medium uppercase">Used RAM</p>
                                            <p className="text-white text-sm font-bold">{memoryInfo.used_ram}</p>
                                        </div>
                                        <Activity className="w-4 h-4 text-blue-400 opacity-70" />
                                    </div>
                                </div>
                                <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 rounded-md border border-green-500/20 p-2.5">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-green-300 text-[10px] font-medium uppercase">Available</p>
                                            <p className="text-white text-sm font-bold">{memoryInfo.available_ram}</p>
                                        </div>
                                        <CheckCircle className="w-4 h-4 text-green-400 opacity-70" />
                                    </div>
                                </div>
                                <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 rounded-md border border-orange-500/20 p-2.5">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-orange-300 text-[10px] font-medium uppercase">Usage</p>
                                            <p className="text-white text-sm font-bold">{memoryInfo.memory_usage}</p>
                                        </div>
                                        <Cpu className="w-4 h-4 text-orange-400 opacity-70" />
                                    </div>
                                    <div className="mt-1.5">
                                        <div className="w-full bg-gray-700/50 rounded-full h-1">
                                            <div
                                                className="bg-orange-400 h-1 rounded-full transition-all duration-300"
                                                style={{ width: memoryUsageWidth }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Memory Modules Section */}
                            <div className="bg-gray-800/40 rounded-md border border-gray-700 overflow-hidden">
                                <div className="px-3 py-2 border-b border-gray-700/50 bg-gray-800/60">
                                    <h3 className="text-xs font-bold text-white flex items-center uppercase tracking-wider">
                                        <Package className="w-3.5 h-3.5 mr-2 text-orange-500" />
                                        Memory Modules ({modules.length})
                                    </h3>
                                </div>
                                <div className="p-2">
                                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                                        {modules.map((module: any, index: number) => (
                                            <div key={index} className="bg-gray-900/30 rounded-md border border-gray-700/50 p-2.5 hover:border-purple-500/30 transition-all duration-200">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center space-x-2 min-w-0">
                                                        <div className="w-2 h-2 bg-purple-400 rounded-full shrink-0"></div>
                                                        <span className="text-white font-bold text-xs truncate">{module.tag || module.device_locator || module.bank_label}</span>
                                                    </div>
                                                    <span className="px-1.5 py-0.5 bg-purple-500/10 text-purple-300 rounded text-[9px] font-bold uppercase whitespace-nowrap">
                                                        {module.bank_label || 'Slot ' + (index + 1)}
                                                    </span>
                                                </div>

                                                <div className="space-y-1 text-[11px]">
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-200">Capacity:</span>
                                                        <span className="text-white font-mono">{module.capacity_gb} GB</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-200">Speed:</span>
                                                        <span className="text-white font-mono">{module.speed || module.configured_clock_speed} MHz</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-200">Type:</span>
                                                        <span className="text-white truncate max-w-[80px] text-right">{module.memory_type || 'Unknown'}</span>
                                                    </div>
                                                </div>

                                                <div className="mt-2 pt-2 border-t border-gray-700/50">
                                                    {expandedSections.has(`memory-module-${index}`) ? (
                                                        <div className="space-y-1 animate-fadeIn text-[10px]">
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-200">Manufacturer:</span>
                                                                <span className="text-white truncate max-w-[100px] text-right">{module.manufacturer || 'Unknown'}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-200">Part #:</span>
                                                                <span className="text-white font-mono truncate max-w-[100px] text-right">{module.part_number}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-200">Serial:</span>
                                                                <span className="text-white font-mono truncate max-w-[100px] text-right">{module.serial_number}</span>
                                                            </div>
                                                            <button
                                                                onClick={() => toggleSection(`memory-module-${index}`)}
                                                                className="w-full mt-2 pt-1 border-t border-gray-700/30 flex items-center justify-center text-purple-400 hover:text-purple-300 text-[10px] font-bold cursor-pointer"
                                                            >
                                                                <ChevronUp className="w-3 h-3 mr-1" />
                                                                HIDE
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => toggleSection(`memory-module-${index}`)}
                                                            className="w-full flex items-center justify-center text-purple-400 hover:text-purple-300 text-[10px] font-bold cursor-pointer"
                                                        >
                                                            <ChevronDown className="w-3 h-3 mr-1" />
                                                            DETAILS
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-8 bg-gray-800/20 rounded-md border border-gray-700/50">
                            <MemoryStick className="w-6 h-6 text-gray-600 mx-auto mb-2 opacity-30" />
                            <h3 className="text-xs font-bold text-purple-300 mb-1">No Memory Information</h3>
                            <p className="text-[10px] text-gray-200">Memory data is not available.</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default MemoryInfo;
